import { Permission, PermissionCategory, PermissionId } from '../../types/rbac';

/**
 * System-wide permission definitions
 */
export const SYSTEM_PERMISSIONS: Permission[] = [
  // General
  {
    id: PermissionId.VIEW_DASHBOARD,
    name: 'View Dashboard',
    description: 'Access to view the dashboard',
    category: PermissionCategory.GENERAL
  },
  
  // System
  {
    id: PermissionId.MANAGE_USERS,
    name: 'Manage Users',
    description: 'Create, edit, and delete users',
    category: PermissionCategory.SYSTEM
  },
  {
    id: PermissionId.MANAGE_STAFF,
    name: 'Manage Staff',
    description: 'Create, edit, and delete staff members',
    category: PermissionCategory.SYSTEM
  },
  {
    id: PermissionId.MANAGE_ROLES,
    name: 'Manage Roles',
    description: 'Create, edit, and delete roles',
    category: PermissionCategory.SYSTEM
  },
  {
    id: PermissionId.MANAGE_SETTINGS,
    name: 'Manage Settings',
    description: 'Modify system settings',
    category: PermissionCategory.SYSTEM
  },
  
  // Staff
  {
    id: PermissionId.VIEW_STAFF,
    name: 'View Staff',
    description: 'View staff member information',
    category: PermissionCategory.STAFF
  },
  {
    id: PermissionId.EDIT_STAFF,
    name: 'Edit Staff',
    description: 'Edit staff member information',
    category: PermissionCategory.STAFF
  },
  {
    id: PermissionId.DELETE_STAFF,
    name: 'Delete Staff',
    description: 'Remove staff members',
    category: PermissionCategory.STAFF
  },
  
  // Customers
  {
    id: PermissionId.MANAGE_CUSTOMERS,
    name: 'Manage Customers',
    description: 'Full access to customer management',
    category: PermissionCategory.CUSTOMERS
  },
  {
    id: PermissionId.VIEW_CUSTOMERS,
    name: 'View Customers',
    description: 'View customer information',
    category: PermissionCategory.CUSTOMERS
  },
  {
    id: PermissionId.EDIT_CUSTOMERS,
    name: 'Edit Customers',
    description: 'Edit customer information',
    category: PermissionCategory.CUSTOMERS
  },
  
  // Equipment
  {
    id: PermissionId.MANAGE_VESSELS,
    name: 'Manage Vessels',
    description: 'Full access to vessel management',
    category: PermissionCategory.EQUIPMENT
  },
  {
    id: PermissionId.VIEW_VESSELS,
    name: 'View Vessels',
    description: 'View vessel information',
    category: PermissionCategory.EQUIPMENT
  },
  {
    id: PermissionId.MANAGE_TRAILERS,
    name: 'Manage Trailers',
    description: 'Full access to trailer management',
    category: PermissionCategory.EQUIPMENT
  },
  {
    id: PermissionId.VIEW_TRAILERS,
    name: 'View Trailers',
    description: 'View trailer information',
    category: PermissionCategory.EQUIPMENT
  },
  
  // Operations
  {
    id: PermissionId.MANAGE_MAINTENANCE,
    name: 'Manage Maintenance',
    description: 'Full access to maintenance management',
    category: PermissionCategory.OPERATIONS
  },
  {
    id: PermissionId.VIEW_MAINTENANCE,
    name: 'View Maintenance',
    description: 'View maintenance records',
    category: PermissionCategory.OPERATIONS
  },
  {
    id: PermissionId.MANAGE_RESERVATIONS,
    name: 'Manage Reservations',
    description: 'Full access to reservation management',
    category: PermissionCategory.OPERATIONS
  },
  {
    id: PermissionId.VIEW_RESERVATIONS,
    name: 'View Reservations',
    description: 'View reservation information',
    category: PermissionCategory.OPERATIONS
  },
  
  // Marina
  {
    id: PermissionId.MANAGE_MARINA,
    name: 'Manage Marina',
    description: 'Full access to marina management',
    category: PermissionCategory.MARINA
  },
  {
    id: PermissionId.VIEW_MARINA,
    name: 'View Marina',
    description: 'View marina information',
    category: PermissionCategory.MARINA
  },
  {
    id: PermissionId.MANAGE_LAUNCH,
    name: 'Manage Launch',
    description: 'Full access to launch ramp management',
    category: PermissionCategory.MARINA
  },
  {
    id: PermissionId.VIEW_LAUNCH,
    name: 'View Launch',
    description: 'View launch ramp information',
    category: PermissionCategory.MARINA
  },
  
  // Store
  {
    id: PermissionId.MANAGE_STORE,
    name: 'Manage Store',
    description: 'Full access to store management',
    category: PermissionCategory.STORE
  },
  {
    id: PermissionId.VIEW_STORE,
    name: 'View Store',
    description: 'View store information',
    category: PermissionCategory.STORE
  },
  {
    id: PermissionId.MANAGE_INVENTORY,
    name: 'Manage Inventory',
    description: 'Full access to inventory management',
    category: PermissionCategory.STORE
  },
  {
    id: PermissionId.VIEW_INVENTORY,
    name: 'View Inventory',
    description: 'View inventory information',
    category: PermissionCategory.STORE
  },
  
  // Kitchen
  {
    id: PermissionId.MANAGE_KITCHEN,
    name: 'Manage Kitchen',
    description: 'Full access to kitchen management',
    category: PermissionCategory.KITCHEN
  },
  {
    id: PermissionId.VIEW_KITCHEN,
    name: 'View Kitchen',
    description: 'View kitchen information',
    category: PermissionCategory.KITCHEN
  },
  {
    id: PermissionId.MANAGE_MENU,
    name: 'Manage Menu',
    description: 'Full access to menu management',
    category: PermissionCategory.KITCHEN
  },
  {
    id: PermissionId.VIEW_MENU,
    name: 'View Menu',
    description: 'View menu information',
    category: PermissionCategory.KITCHEN
  },
  
  // Business
  {
    id: PermissionId.MANAGE_PRICING,
    name: 'Manage Pricing',
    description: 'Full access to pricing management',
    category: PermissionCategory.BUSINESS
  },
  {
    id: PermissionId.VIEW_PRICING,
    name: 'View Pricing',
    description: 'View pricing information',
    category: PermissionCategory.BUSINESS
  },
  {
    id: PermissionId.MANAGE_REPORTS,
    name: 'Manage Reports',
    description: 'Full access to report management',
    category: PermissionCategory.BUSINESS
  },
  {
    id: PermissionId.VIEW_REPORTS,
    name: 'View Reports',
    description: 'View business reports',
    category: PermissionCategory.BUSINESS
  },
  
  // Finance
  {
    id: PermissionId.MANAGE_FINANCE,
    name: 'Manage Finance',
    description: 'Full access to financial management',
    category: PermissionCategory.FINANCE
  },
  {
    id: PermissionId.VIEW_FINANCE,
    name: 'View Finance',
    description: 'View financial information',
    category: PermissionCategory.FINANCE
  },
  {
    id: PermissionId.MANAGE_INVOICES,
    name: 'Manage Invoices',
    description: 'Full access to invoice management',
    category: PermissionCategory.FINANCE
  },
  {
    id: PermissionId.VIEW_INVOICES,
    name: 'View Invoices',
    description: 'View invoice information',
    category: PermissionCategory.FINANCE
  }
];

/**
 * Get all available permissions in the system
 */
export function getAllPermissions(): Permission[] {
  return SYSTEM_PERMISSIONS;
}

/**
 * Get permissions by category
 */
export function getPermissionsByCategory(category: PermissionCategory): Permission[] {
  return SYSTEM_PERMISSIONS.filter(p => p.category === category);
}

/**
 * Get a permission by its ID
 */
export function getPermissionById(id: PermissionId): Permission | undefined {
  return SYSTEM_PERMISSIONS.find(permission => permission.id === id);
}

/**
 * Get all available categories
 */
export function getAllCategories(): PermissionCategory[] {
  return Object.values(PermissionCategory);
}

/**
 * Validate if a permission ID exists in the system
 */
export function isValidPermissionId(id: string): id is PermissionId {
  return Object.values(PermissionId).includes(id as PermissionId);
}

/**
 * Get all permissions for a given list of permission IDs
 */
export function getPermissionsFromIds(ids: string[]): Permission[] {
  return SYSTEM_PERMISSIONS.filter(permission => ids.includes(permission.id));
} 